import { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface LabelValueOption {
  label: string
  value: string
}

function useReviewLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const HOTEL_DESCRIBE_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'C6c2AisU4ipp3p8vRco3',
          defaultMessage: 'Relaxing',
        }),
        value: 'relaxing',
      },
      {
        label: intl.formatMessage({
          id: 'qYRxYx-X45nIgVwHU3cL',
          defaultMessage: 'Comfortable',
        }),
        value: 'comfortable',
      },
      {
        label: intl.formatMessage({
          id: 'SlPCO-XI8zC1UVVuHmgT',
          defaultMessage: 'Clean',
        }),
        value: 'clean',
      },
      {
        label: intl.formatMessage({
          id: 'nja_5SXuOKxUsFk4QnQu',
          defaultMessage: 'Value for money',
        }),
        value: 'value_for_money',
      },
      {
        label: intl.formatMessage({
          id: 'L2cqjyd8ZLZTFZ1jNUv8',
          defaultMessage: 'Good facilities',
        }),
        value: 'good_facilities',
      },
      {
        label: intl.formatMessage({
          id: 'uCSEsTy7jNdrm8wMNql3',
          defaultMessage: 'Kid friendly',
        }),
        value: 'kid_friendly',
      },
    ]

    const EXPERIENCE_DESCRIBE_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: '6ZR3lD2yEt-sl_XIjJVO',
          defaultMessage: 'Relaxing',
        }),
        value: 'relaxing',
      },
      {
        label: intl.formatMessage({
          id: 'f6LqnwDWg9--AzAjAahX',
          defaultMessage: 'Comfortable',
        }),
        value: 'comfortable',
      },
      {
        label: intl.formatMessage({
          id: '4VhEUTbQlwncZARR01ex',
          defaultMessage: 'Clean',
        }),
        value: 'clean',
      },
      {
        label: intl.formatMessage({
          id: 'HzDtamoPLUmppTJ9_f9m',
          defaultMessage: 'Variety of meals',
        }),
        value: 'variety_of_meals',
      },
      {
        label: intl.formatMessage({
          id: 'F06CFXivwfLrnL5AdFtz',
          defaultMessage: 'Good value',
        }),
        value: 'good_value',
      },
      {
        label: intl.formatMessage({
          id: 'NAbxuCd_EyP2QFqmdl_G',
          defaultMessage: 'On-time',
        }),
        value: 'on_time',
      },
    ]

    const TOUR_DESCRIBE_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'j1woZfAw_gQ5Qas3WghB',
          defaultMessage: 'Culturally immersive',
        }),
        value: 'culturally_immersive',
      },
      {
        label: intl.formatMessage({
          id: 'N1EW9pqNq46vC0ybaDwB',
          defaultMessage: 'Comfortable & convenient',
        }),
        value: 'comfortable_convenient',
      },
      {
        label: intl.formatMessage({
          id: '5NI4OJIeYzCcCWBczAjs',
          defaultMessage: 'Engaging itinerary',
        }),
        value: 'engaging_itinerary',
      },
      {
        label: intl.formatMessage({
          id: 's8cF3SAmV4_I3dU8rdlf',
          defaultMessage: 'Expert guides',
        }),
        value: 'expert_guides',
      },
      {
        label: intl.formatMessage({
          id: 'kEFjQO52Yubk3H21q0-q',
          defaultMessage: 'Flexible options',
        }),
        value: 'flexible_options',
      },
      {
        label: intl.formatMessage({
          id: 'LyJbZkvYXH6FbXjXqJYM',
          defaultMessage: 'Hidden gems',
        }),
        value: 'hidden_gems',
      },
      {
        label: intl.formatMessage({
          id: '3m3b-FnHXFkNW_rfeAAX',
          defaultMessage: 'Iconic landmarks',
        }),
        value: 'iconic_landmarks',
      },
      {
        label: intl.formatMessage({
          id: 'icXP99RLRanVoYtNAuKd',
          defaultMessage: 'Good value',
        }),
        value: 'good_value',
      },
      {
        label: intl.formatMessage({
          id: '2ofII9O3JIITfxxrZMcg',
          defaultMessage: 'Luxurious',
        }),
        value: 'luxurious',
      },
      {
        label: intl.formatMessage({
          id: 'VxUHQeP6hgxfm9MfSXMN',
          defaultMessage: 'Memorable',
        }),
        value: 'memorable',
      },
      {
        label: intl.formatMessage({
          id: '_3UbngkK69YfKevJb0gd',
          defaultMessage: 'Scenic beauty',
        }),
        value: 'scenic',
      },
      {
        label: intl.formatMessage({
          id: 'dLcbdjRMf8yY08jj1C-2',
          defaultMessage: 'Safe & secure',
        }),
        value: 'safe_secure',
      },
    ]

    const COMPANIONSHIP_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'rRi6xVjSpI1WcNSXvlzl',
          defaultMessage: 'Just myself',
        }),
        value: 'alone',
      },
      {
        label: intl.formatMessage({
          id: 'QKAp2IpBCiMLCogZKysQ',
          defaultMessage: 'Partner',
        }),
        value: 'partner',
      },
      {
        label: intl.formatMessage({
          id: 'Ryi2w01Xk5H_625U1wMv',
          defaultMessage: 'Family',
        }),
        value: 'family',
      },
      {
        label: intl.formatMessage({
          id: 'qHUXDj2j81lriaQ-C9uo',
          defaultMessage: 'Friends',
        }),
        value: 'friends',
      },
      {
        label: intl.formatMessage({
          id: 'Bt208m8gelZlHSd7Vp6Y',
          defaultMessage: 'Other',
        }),
        value: 'other',
      },
    ]

    const COMPANIONSHIP_ENUM: Record<string, string> = {
      alone: intl.formatMessage({
        id: 'w44SxB7XLzGn7gs9UFem',
        defaultMessage: 'Just myself',
      }),
      partner: intl.formatMessage({
        id: 'pzPa2uUo-7uJ32AoVbyl',
        defaultMessage: 'Partner',
      }),
      family: intl.formatMessage({
        id: 'f72hg6PRgfKsGFrrpXDo',
        defaultMessage: 'Family',
      }),
      friends: intl.formatMessage({
        id: 'V00LbY-3I_KVpIpl-Evv',
        defaultMessage: 'Friends',
      }),
      other: intl.formatMessage({
        id: 'A5uyi7t3T2foeKrk_GX9',
        defaultMessage: 'Other',
      }),
    }

    const DEFINITION_ENUM: Record<string, string> = {
      relaxing: intl.formatMessage({
        id: 'ParncI34gQbX-ZZ8UnPI',
        defaultMessage: 'Relaxing',
      }),
      comfortable: intl.formatMessage({
        id: 'PC7LgsZCmgbFovNHL8kH',
        defaultMessage: 'Comfortable',
      }),
      clean: intl.formatMessage({
        id: 'FReBOf1oT_O77qMqsTW4',
        defaultMessage: 'Clean',
      }),
      value_for_money: intl.formatMessage({
        id: 'bKjhaDJ5tsDCg_6fFHrh',
        defaultMessage: 'Value for money',
      }),
      good_facilities: intl.formatMessage({
        id: 'HuSp5TEwvSPIE7-YgPKB',
        defaultMessage: 'Good facilities',
      }),
      kid_friendly: intl.formatMessage({
        id: 'Yle9f-uexyHLoRF3rQpw',
        defaultMessage: 'Kid friendly',
      }),
      variety_of_meals: intl.formatMessage({
        id: 'WhCcEbZRzcehniNshVqd',
        defaultMessage: 'Variety of meals',
      }),
      good_value: intl.formatMessage({
        id: '7onm2uBraXBqASILgSUt',
        defaultMessage: 'Good value',
      }),
      on_time: intl.formatMessage({
        id: 'OiSckaW6ZS2uYnrjTmKq',
        defaultMessage: 'On-time',
      }),
      expert_guides: intl.formatMessage({
        id: '8Jn6eqHMRR7fKETKkEzR',
        defaultMessage: 'Expert guides',
      }),
      scenic: intl.formatMessage({
        id: 'Y9VAohmkMIxgKzPfF-Ly',
        defaultMessage: 'Scenic beauty',
      }),
      immersive: intl.formatMessage({
        id: 'efsOyLKx8Xxsw9E3wQla',
        defaultMessage: 'Immersive',
      }),
      luxurious: intl.formatMessage({
        id: 'wxIaxMmSFadYu9mjQ-At',
        defaultMessage: 'Luxurious',
      }),
      variety_of_activities: intl.formatMessage({
        id: 'EkReqW8zLcV-uKmNTQRx',
        defaultMessage: 'Variety of activities',
      }),
      culturally_immersive: intl.formatMessage({
        id: 'ywW91mzjfYB8SNlm9_bC',
        defaultMessage: 'Culturally immersive',
      }),
      comfortable_convenient: intl.formatMessage({
        id: 'p_-KqL9PLJL3Agn6xPf7',
        defaultMessage: 'Comfortable & convenient',
      }),
      engaging_itinerary: intl.formatMessage({
        id: 'LoXFB2o7n2yEpMJlPfwR',
        defaultMessage: 'Engaging itinerary',
      }),
      flexible_options: intl.formatMessage({
        id: '0k6z7P6F_Cu5PG0gTE0A',
        defaultMessage: 'Flexible options',
      }),
      hidden_gems: intl.formatMessage({
        id: 'rIgHEX5fPyYuGQvuhfhp',
        defaultMessage: 'Hidden gems',
      }),
      iconic_landmarks: intl.formatMessage({
        id: 'NNWORj-fJlqbu2eeAKGp',
        defaultMessage: 'Iconic landmarks',
      }),
      memorable: intl.formatMessage({
        id: 'A_-JfU0AFffwT5gjbGzR',
        defaultMessage: 'Memorable',
      }),
      safe_secure: intl.formatMessage({
        id: 'ixSlO26FyYUBBLYOOpgk',
        defaultMessage: 'Safe & secure',
      }),
    }

    const SORT_OPTION_NEWEST: LabelValueOption = {
      label: intl.formatMessage({
        id: 'PEVEv38cEHP_X7XkfPbo',
        defaultMessage: 'Most recent',
      }),
      value: 'newest',
    }

    const SORT_OPTION_OLDEST: LabelValueOption = {
      label: intl.formatMessage({
        id: '47rR9pdxgKOp2OghIrse',
        defaultMessage: 'Oldest',
      }),
      value: 'oldest',
    }

    const SORT_OPTION_BEST_RATING: LabelValueOption = {
      label: intl.formatMessage({
        id: 'sLxLlkNMVzZScR91F57h',
        defaultMessage: 'Highest rating',
      }),
      value: 'best_rating',
    }

    const SORT_OPTION_WORST_RATING: LabelValueOption = {
      label: intl.formatMessage({
        id: 'ZllPY9DeCOBxfGjqStdY',
        defaultMessage: 'Lowest rating',
      }),
      value: 'worst_rating',
    }

    const SORT_OPTION_MOST_RELEVANT: LabelValueOption = {
      label: intl.formatMessage({
        id: 'bcWagswgT4stzuw8E59b',
        defaultMessage: 'Most relevant',
      }),
      value: 'most_relevant',
    }

    const userReviewsSortOptions: Array<LabelValueOption> = [
      SORT_OPTION_MOST_RELEVANT,
      SORT_OPTION_NEWEST,
      SORT_OPTION_OLDEST,
      SORT_OPTION_BEST_RATING,
      SORT_OPTION_WORST_RATING,
    ]

    const REVIEW_PANEL_SORT_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'kTKMlRlGh0A8I7puWpUu',
          defaultMessage: 'Most recent',
        }),
        value: 'newest',
      },
      {
        label: intl.formatMessage({
          id: '7vCAWYMnmbpydBd1hpjZ',
          defaultMessage: 'Highest rated',
        }),
        value: 'best_rating',
      },
      {
        label: intl.formatMessage({
          id: 'wctOZaI9GR5_ooG92Wab',
          defaultMessage: 'Lowest rated',
        }),
        value: 'worst_rating',
      },
      {
        label: intl.formatMessage({
          id: 'huKfCm8v6WpIk9Xo5iUE',
          defaultMessage: 'Recommended',
        }),
        value: 'recommend',
      },
    ]

    const REVIEW_PANEL_RATING_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'Q6iXxLsONcwrBvACHlcl',
          defaultMessage: 'All',
        }),
        value: '',
      },
      {
        label: intl.formatMessage({
          id: 'TxjymdKJRlSmoOJT9y-s',
          defaultMessage: 'Exceptional (9-10)',
        }),
        value: 'exceptional',
      },
      {
        label: intl.formatMessage({
          id: '7V2jRS9x1xWpm4WG4jAi',
          defaultMessage: 'Good (7-8)',
        }),
        value: 'good',
      },
      {
        label: intl.formatMessage({
          id: 'VTOuKlETAVgMZ3tAnuHF',
          defaultMessage: 'Okay (5-6)',
        }),
        value: 'okay',
      },
      {
        label: intl.formatMessage({
          id: 'MFG9OLyMbyXM42bgssud',
          defaultMessage: 'Poor (3-4)',
        }),
        value: 'poor',
      },
      {
        label: intl.formatMessage({
          id: 'jsqGBo_EaOqeYF4A4KfW',
          defaultMessage: 'Terrible (1-2)',
        }),
        value: 'very-poor',
      },
    ]

    const REVIEW_PANEL_TRAVELLER_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: '_2DuFNIP9t8C1dLnwSQP',
          defaultMessage: 'All',
        }),
        value: '',
      },
      {
        label: intl.formatMessage({
          id: 'MeAy7karfeoVbl5p3Kdz',
          defaultMessage: 'Partner',
        }),
        value: 'partner',
      },
      {
        label: intl.formatMessage({
          id: 'Nz_0QxgieAXs_WkYl3qT',
          defaultMessage: 'Family',
        }),
        value: 'family',
      },
      {
        label: intl.formatMessage({
          id: 'boFxL3Dl0CFSYaZkZMdV',
          defaultMessage: 'Friends',
        }),
        value: 'friends',
      },
      {
        label: intl.formatMessage({
          id: 'Mqz7mB3ind_MEUl93ysY',
          defaultMessage: 'Just myself',
        }),
        value: 'alone',
      },
    ]

    return {
      HOTEL_DESCRIBE_OPTIONS,
      EXPERIENCE_DESCRIBE_OPTIONS,
      TOUR_DESCRIBE_OPTIONS,
      COMPANIONSHIP_OPTIONS,
      COMPANIONSHIP_ENUM,
      DEFINITION_ENUM,
      userReviewsSortOptions,
      SORT_OPTION_MOST_RELEVANT,
      REVIEW_PANEL_SORT_OPTIONS,
      REVIEW_PANEL_RATING_OPTIONS,
      REVIEW_PANEL_TRAVELLER_OPTIONS,
    }
  }, [intl])
}

export default useReviewLabels
